import { c as _c } from "react/compiler-runtime";
import { useEffect } from 'react';
import classnames from 'classnames';
import dynamic from 'next/dynamic';
import { batch, connect } from 'react-redux';
import { Category, getMacroOfCategoryID } from '@sbt-web/category-utils';
import { useViewport } from '@sbt-web/hooks';
import { AdTypes, CategoryID, type Faq as FaqType, GeoEntry, SeoMetaPayload } from '@sbt-web/network/types';
import type { RecentSearchOption } from '@sbt-web/search-autocomplete';
import { Button, DeviceWidth, FullModal } from '@sbt-web/ui';
import { readFromLocalStorage, TuttaItalia } from '@sbt-web/utils';
import { LiraAdvContainer } from '@sbt-web/adv';
import { LiraContainerIds } from '@/client/components/Adv/Lira/liraContainerIds';
import { LiraLazySlotBelowListing } from '@/client/components/Adv/Lira/LiraLazySlotBelowListing';
import { FloatingButtonTrackingEvents } from '@/client/components/Scaffolding/FloatingButton';
import { SnackBarTrackingEvents } from '@/client/components/Scaffolding/SnackBar';
import ListingBreadcrumbs from '@/client/components/SEO/Breadcrumbs/ListingBreadcrumbs';
import MostSearched from '@/client/components/SEO/InternalLinks/MostSearched';
import { ListingLinks } from '@/client/components/SEO/InternalLinks/types';
import Sitemap from '@/client/components/SEO/Sitemap';
import type { FiltersConfigState } from '@/reducers/filtersConfig';
import type { RootState } from '@/reducers/index';
import type { ItemsState } from '@/reducers/items';
import { declareFinalSearch, type RadiusSearch, resetFilters, type SearchState, setFiltersDialogStatus } from '@/reducers/search';
import { setPreventPopstate } from '@/reducers/system';
import { onEngage, onSave, onUnsave } from '@/shared/helpers/Favorites/trackingEvents';
import { useSavedSearches } from '@/shared/helpers/SavedSearches';
import { areSameGeo, areSameRadius } from '@/tools/geoValuesHelper';
import InternalLinksFooter from '../../SEO/InternalLinks/Footer';
import InternalLinksHeader from '../../SEO/InternalLinks/Header';
import InternalLinksSidebar from '../../SEO/InternalLinks/Sidebar';
import Faq from '../Elements/Faq';
import PaginationSelector from '../Elements/Pagination';
import SearchBar from '../Elements/SearchBar';
import TopBar from '../Elements/TopBar';
import ListingShallowRouter from '../ListingShallowRouter';
import FavoritesContainer from './FavoritesContainer';
import ItemListContainer from './ItemListContainer';
import gridStyles from '@/client/components/Detail/Containers/style/grid.module.scss';
import ownClasses from './ListingContainer.module.scss';
const FiltersContainer = dynamic(() => import('./FiltersContainer'), {
  ssr: false
});
const FloatingButton = dynamic(() => import('@/client/components/Scaffolding/FloatingButton'), {
  ssr: false
});
const SnackBar = dynamic(() => import('@/client/components/Scaffolding/SnackBar'), {
  ssr: false
});
const TextLinkBox = dynamic(() => import('./TextLinkBox/TextLinkBox'), {
  ssr: false
});
interface OwnProps {
  ads?: string[];
  seo?: SeoMetaPayload;
  handleItemClick: () => void;
  internalLinks: ListingLinks | null;
  noAdvToggle?: string | null;
}
interface StateProps {
  filtersDialogOpen?: boolean;
  query?: string;
  category: Category;
  isRadiusSearch?: boolean;
  geo: GeoEntry;
  searchNearRegions: boolean;
  filters: Record<string, string>;
  qso: boolean;
  shp: boolean;
  urg: boolean;
  adType: AdTypes;
  radiusSearch: RadiusSearch | null;
  search: SearchState;
  items: ItemsState;
  filtersConfig: FiltersConfigState;
}
interface DispatchProps {
  openFiltersDialog: () => void;
  resetFilters: () => void;
  acceptSearch: () => void;
}
interface LCFaqProps {
  faqData: FaqType | null;
}
export type Props = OwnProps & StateProps & DispatchProps & LCFaqProps;
const localStorageKey = 'recent_autocomplete_searches';
const storeRecentSearch = (recentSearch: RecentSearchOption) => {
  try {
    const recentSearches = readFromLocalStorage<RecentSearchOption[]>(localStorageKey, []);
    if (recentSearch.query === '' && recentSearch.category.id === CategoryID.Tutte && recentSearch.geo.geoValues.region.id === TuttaItalia.id) {
      return;
    } else if (recentSearches?.length === 0) {
      window.localStorage.setItem(localStorageKey, JSON.stringify([recentSearch]));
    } else {
      const olderSearches = recentSearches?.filter(olderSearch => {
        const queryUnequal = olderSearch.query !== recentSearch.query;
        const categoryUnequal = olderSearch.category.id !== recentSearch.category.id;
        const nearRegionsUnequal = olderSearch.searchNearRegions !== recentSearch.searchNearRegions;
        const geoUnequal = !areSameGeo(olderSearch.geo.geoValues, recentSearch.geo.geoValues, olderSearch.searchNearRegions, recentSearch.searchNearRegions);
        const radiusUnequal = !areSameRadius(olderSearch.geo.radiusValues, recentSearch.geo.radiusValues);
        return queryUnequal || categoryUnequal || nearRegionsUnequal || geoUnequal || radiusUnequal;
      });
      // Put the search at the top as the most recent one and take 2 elements
      window.localStorage.setItem(localStorageKey, JSON.stringify([recentSearch, ...(olderSearches ?? [])].slice(0, 3)));
    }
  } catch {
    console.error('Could not set recent searches');
  }
};
export const ListingContainer = props => {
  const $ = _c(92);
  const {
    isMobile,
    isDesktop
  } = useViewport();
  const {
    openFiltersDialog,
    resetFilters,
    acceptSearch,
    handleItemClick
  } = props;
  const {
    filtersDialogOpen: t0,
    seo,
    internalLinks,
    faqData,
    query,
    category,
    geo,
    searchNearRegions,
    filters,
    qso,
    shp,
    adType,
    radiusSearch,
    search,
    urg,
    items,
    noAdvToggle
  } = props;
  const filtersDialogOpen = t0 === undefined ? false : t0;
  let t1;
  let t2;
  if ($[0] !== adType || $[1] !== category || $[2] !== filters || $[3] !== geo || $[4] !== isMobile || $[5] !== qso || $[6] !== query || $[7] !== radiusSearch || $[8] !== searchNearRegions || $[9] !== shp || $[10] !== urg) {
    t1 = () => {
      if (isMobile && radiusSearch != null) {
        return;
      } else {
        if (query === undefined || !/\S/.test(query)) {
          return;
        } else {
          storeRecentSearch({
            adType,
            query,
            category,
            geo: {
              geoValues: geo,
              radiusValues: radiusSearch
            },
            searchNearRegions,
            allFilters: {
              qso,
              shp,
              urg,
              filters
            }
          });
        }
      }
    };
    t2 = [adType, query, category, geo, filters, qso, shp, urg, radiusSearch, searchNearRegions, isMobile];
    $[0] = adType;
    $[1] = category;
    $[2] = filters;
    $[3] = geo;
    $[4] = isMobile;
    $[5] = qso;
    $[6] = query;
    $[7] = radiusSearch;
    $[8] = searchNearRegions;
    $[9] = shp;
    $[10] = urg;
    $[11] = t1;
    $[12] = t2;
  } else {
    t1 = $[11];
    t2 = $[12];
  }
  useEffect(t1, t2);
  let t3;
  if ($[13] !== isMobile) {
    t3 = isMobile ? new FloatingButtonTrackingEvents() : new SnackBarTrackingEvents();
    $[13] = isMobile;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  const savedSearchProvider = useSavedSearches(t3);
  let t4;
  let t5;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <style>{`
        @media (max-width: ${DeviceWidth.mobile.max}) {
          /* This should be the banner for apps flyer for the installation
          of the APP */
          #af-smart-banner.afb-container {
            z-index: 101;
          }
        }
      `}</style>;
    t5 = <NoScriptSeoCss />;
    $[15] = t4;
    $[16] = t5;
  } else {
    t4 = $[15];
    t5 = $[16];
  }
  let t6;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <ListingShallowRouter />;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== internalLinks) {
    t7 = internalLinks !== null ? <InternalLinksHeader links={internalLinks.header} /> : null;
    $[18] = internalLinks;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== search) {
    t8 = <ListingBreadcrumbs search={search} />;
    $[20] = search;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== openFiltersDialog) {
    t9 = <SearchBar onFiltersOpen={openFiltersDialog} />;
    $[22] = openFiltersDialog;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== t7 || $[25] !== t8 || $[26] !== t9) {
    t10 = <div data-google-interstitial="false">{t6}{t7}{t8}{t9}</div>;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== isDesktop) {
    t11 = isDesktop && <Skyscrapers />;
    $[28] = isDesktop;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== isDesktop || $[31] !== noAdvToggle) {
    t12 = isDesktop && noAdvToggle !== "B" && <LiraAdvContainer className={classnames(gridStyles["detail-component"], ownClasses.listingTop)} style={{
      height: "250px",
      alignContent: "center"
    }} withPlaceholder={true} idForAd={LiraContainerIds.listing.top} />;
    $[30] = isDesktop;
    $[31] = noAdvToggle;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <TopBar />;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== t12) {
    t14 = <div data-google-interstitial="false">{t12}{t13}</div>;
    $[34] = t12;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = classnames(ownClasses.container);
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== internalLinks || $[38] !== isMobile || $[39] !== noAdvToggle) {
    t16 = isMobile ? null : <div className={classnames(ownClasses.col, ownClasses.filters, "col", "filters")}><div data-google-interstitial="false"><FiltersContainer />{noAdvToggle !== "B" && <TextLinkBox page="listing" max_number_textlinks={1} />}</div>{noAdvToggle !== "B" && <div id="banner_container" className={ownClasses.adv}><LiraAdvContainer idForAd={LiraContainerIds.listing.aside} style={{
          display: "block"
        }} /></div>}{internalLinks !== null ? <div data-google-interstitial="false"><InternalLinksSidebar links={internalLinks.sidebar} /></div> : null}</div>;
    $[37] = internalLinks;
    $[38] = isMobile;
    $[39] = noAdvToggle;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== acceptSearch || $[42] !== filtersDialogOpen || $[43] !== isMobile || $[44] !== resetFilters) {
    t17 = isMobile ? <FullModal title="Filtri ricerca" onClose={acceptSearch} headerCta={<Button onClick={() => {
      resetFilters();
    }} design="text" size="small">Azzera</Button>} open={filtersDialogOpen}><FiltersContainer onApply={acceptSearch} /></FullModal> : null;
    $[41] = acceptSearch;
    $[42] = filtersDialogOpen;
    $[43] = isMobile;
    $[44] = resetFilters;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = classnames(ownClasses.col, ownClasses.items, "col", "items");
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  let t19;
  if ($[47] !== search.category.id) {
    t19 = getMacroOfCategoryID(search.category.id);
    $[47] = search.category.id;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  let t20;
  if ($[49] !== handleItemClick || $[50] !== seo || $[51] !== t19.friendly) {
    t20 = <FavoritesContainer onSave={_temp} onUnsave={_temp2} onEngage={onEngage}><ItemListContainer seo={seo} macroCategory={t19.friendly} handleItemClick={handleItemClick} /></FavoritesContainer>;
    $[49] = handleItemClick;
    $[50] = seo;
    $[51] = t19.friendly;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  let t21;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <PaginationSelector />;
    $[53] = t21;
  } else {
    t21 = $[53];
  }
  let t22;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <div data-google-interstitial="false"><LiraLazySlotBelowListing /></div>;
    $[54] = t22;
  } else {
    t22 = $[54];
  }
  let t23;
  if ($[55] !== isMobile || $[56] !== items) {
    t23 = isMobile && items.total > 0 && <div data-google-interstitial="false"><div id={LiraContainerIds.listing.box3} /></div>;
    $[55] = isMobile;
    $[56] = items;
    $[57] = t23;
  } else {
    t23 = $[57];
  }
  let t24;
  if ($[58] !== isMobile || $[59] !== items || $[60] !== props.filtersConfig || $[61] !== props.search || $[62] !== savedSearchProvider) {
    t24 = isMobile ? <FloatingButton search={props.search} filtersConfig={props.filtersConfig} items={items} savedSearchesProvider={savedSearchProvider} /> : <SnackBar search={props.search} filtersConfig={props.filtersConfig} items={items} savedSearchesProvider={savedSearchProvider} />;
    $[58] = isMobile;
    $[59] = items;
    $[60] = props.filtersConfig;
    $[61] = props.search;
    $[62] = savedSearchProvider;
    $[63] = t24;
  } else {
    t24 = $[63];
  }
  let t25;
  if ($[64] !== t20 || $[65] !== t23 || $[66] !== t24) {
    t25 = <div className={t18} data-testid="listing-container">{t20}{t21}{t22}{t23}{t24}</div>;
    $[64] = t20;
    $[65] = t23;
    $[66] = t24;
    $[67] = t25;
  } else {
    t25 = $[67];
  }
  let t26;
  if ($[68] !== t16 || $[69] !== t17 || $[70] !== t25) {
    t26 = <div className={t15}>{t16}{t17}{t25}</div>;
    $[68] = t16;
    $[69] = t17;
    $[70] = t25;
    $[71] = t26;
  } else {
    t26 = $[71];
  }
  let t27;
  if ($[72] !== t14 || $[73] !== t26) {
    t27 = <div className={ownClasses.layout__main}>{t14}{t26}</div>;
    $[72] = t14;
    $[73] = t26;
    $[74] = t27;
  } else {
    t27 = $[74];
  }
  let t28;
  if ($[75] !== t11 || $[76] !== t27) {
    t28 = <div className={ownClasses.layout}>{t11}{t27}</div>;
    $[75] = t11;
    $[76] = t27;
    $[77] = t28;
  } else {
    t28 = $[77];
  }
  let t29;
  if ($[78] !== faqData) {
    t29 = !!faqData?.faq?.length && <Faq data={faqData} />;
    $[78] = faqData;
    $[79] = t29;
  } else {
    t29 = $[79];
  }
  let t30;
  if ($[80] !== internalLinks?.sidebar || $[81] !== isMobile) {
    t30 = internalLinks?.sidebar && isMobile && <MostSearched links={internalLinks?.sidebar} />;
    $[80] = internalLinks?.sidebar;
    $[81] = isMobile;
    $[82] = t30;
  } else {
    t30 = $[82];
  }
  let t31;
  if ($[83] !== internalLinks) {
    t31 = internalLinks?.footer && <InternalLinksFooter links={internalLinks.footer} />;
    $[83] = internalLinks;
    $[84] = t31;
  } else {
    t31 = $[84];
  }
  let t32;
  if ($[85] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <Sitemap />;
    $[85] = t32;
  } else {
    t32 = $[85];
  }
  let t33;
  if ($[86] !== t10 || $[87] !== t28 || $[88] !== t29 || $[89] !== t30 || $[90] !== t31) {
    t33 = <div>{t4}{t5}{t10}{t28}{t29}{t30}{t31}{t32}</div>;
    $[86] = t10;
    $[87] = t28;
    $[88] = t29;
    $[89] = t30;
    $[90] = t31;
    $[91] = t33;
  } else {
    t33 = $[91];
  }
  return t33;
};
export default connect<StateProps, DispatchProps, OwnProps, RootState>(state => ({
  filtersDialogOpen: state.search.filtersDialogOpen,
  category: state.search.category,
  filters: state.search.filters,
  geo: state.search.geo,
  query: state.search.query,
  isRadiusSearch: state.search.radiusSearch != null,
  qso: state.search.qso,
  shp: state.search.includeShippableOnly,
  adType: state.search.adType,
  radiusSearch: state.search.radiusSearch || null,
  searchNearRegions: state.search.searchNearRegions ?? false,
  search: state.search,
  urg: state.search.includeUrgent,
  items: state.items,
  filtersConfig: state.filtersConfig
}), dispatch => ({
  openFiltersDialog: () => {
    batch(() => {
      dispatch(setFiltersDialogStatus(true));
      dispatch(setPreventPopstate(true));
    });
  },
  acceptSearch: () => {
    batch(() => {
      dispatch(setFiltersDialogStatus(false));
      dispatch(setPreventPopstate(false));
      dispatch(declareFinalSearch());
    });
  },
  resetFilters: () => {
    dispatch(resetFilters());
  }
}))(ListingContainer);
const NoScriptSeoCss = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <noscript><style>{"\n        #banner_container {\n          flex: 0 !important;\n        }\n      "}</style></noscript>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const Skyscrapers = () => {
  const $ = _c(4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = classnames(ownClasses.layout__aside, ownClasses["layout__aside--left"]);
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <aside className={t0}><div className={ownClasses.layout__aside__wrapper}><div className={ownClasses.layout__aside__skyscraper} data-google-interstitial="false"><LiraAdvContainer className={ownClasses.skyscrapers} idForAd={LiraContainerIds.listing.skyscraperLeft} /></div></div></aside>;
    t2 = classnames(ownClasses.layout__aside, ownClasses["layout__aside--right"]);
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <>{t1}<aside className={t2}><div className={ownClasses.layout__aside__wrapper}><div className={ownClasses.layout__aside__skyscraper} data-google-interstitial="false"><LiraAdvContainer className={ownClasses.skyscrapers} idForAd={LiraContainerIds.listing.skyscraperRight} /></div></div></aside></>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
};
function _temp(urn) {
  onSave(urn, "listing");
}
function _temp2(urn_0) {
  return onUnsave(urn_0, "listing");
}